import { useEffect, useMemo, useState } from "react"

import type { PlotTabsConfig } from "types/dashboard.types"

export const ALL_TAB = "all"

export interface PlotTabVisibility {
  activeTab: string
  setActiveTab: (tab: string) => void
  visibleFields: Set<string>
}

/**
 * Manages tab-based field visibility for a plot.
 *
 * - When the active tab is a known tab id, only that tab's fields are visible.
 * - When the active tab is "all", all fields across all tabs are visible simultaneously.
 * - When `tabsConfig` is undefined, returns an empty visibility state (no fields shown).
 */
export function usePlotTabVisibility(
  tabsConfig: PlotTabsConfig | undefined,
): PlotTabVisibility {
  const defaultTab = tabsConfig?.defaultTab ?? tabsConfig?.tabs[0]?.id ?? ALL_TAB

  const [activeTab, setActiveTab] = useState<string>(defaultTab)

  useEffect(() => {
    setActiveTab(tabsConfig?.defaultTab ?? tabsConfig?.tabs[0]?.id ?? ALL_TAB)
  }, [tabsConfig])

  const visibleFields = useMemo((): Set<string> => {
    if (!tabsConfig) return new Set()

    const isAll =
      activeTab === ALL_TAB || !tabsConfig.tabs.some((t) => t.id === activeTab)
    if (isAll) {
      return new Set(tabsConfig.tabs.flatMap((t) => t.fields))
    }

    const tab = tabsConfig.tabs.find((t) => t.id === activeTab)
    return new Set(tab?.fields ?? [])
  }, [tabsConfig, activeTab])

  return { activeTab, setActiveTab, visibleFields }
}
